"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useEnergyAggregation = void 0;
const react_1 = require("react");
const useAlertChangeRefresh_1 = require("./useAlertChangeRefresh");
const queries_1 = require("../../graphql/queries");
const energy_1 = require("../../utils/aggregation/energy");
const graphqlCache_1 = require("../../utils/graphqlCache");
const stableValue_1 = require("../../utils/stableValue");
const useStableList_1 = require("../useStableList");
const POLLING_INTERVAL_MS = 30_000;
// Limit big enough to reliably include packets B (branch_ct1-8) and C (branch_ct9-16).
const EVENT_FETCH_LIMIT = 10;
const useEnergyAggregation = (hubId, allSensors) => {
    const sensors = allSensors ?? [];
    const [fetchResults, setFetchResults] = (0, react_1.useState)(new Map());
    const [loading, setLoading] = (0, react_1.useState)(true);
    const lastFetchTime = (0, react_1.useRef)(0);
    const intervalRef = (0, react_1.useRef)(null);
    const powerSensors = (0, useStableList_1.useStableList)((0, energy_1.selectPowerSensors)(sensors));
    const fetchAllSensorData = (0, react_1.useCallback)(async (force = false) => {
        if (!hubId || powerSensors.length === 0) {
            setFetchResults(new Map());
            setLoading(false);
            return;
        }
        const now = Date.now();
        if (!force && now - lastFetchTime.current < 5000)
            return;
        lastFetchTime.current = now;
        if (force)
            (0, graphqlCache_1.clearGraphqlCache)();
        try {
            const results = await Promise.all(powerSensors.map(async (sensor) => {
                try {
                    const [eventResponse, alertResponse] = await Promise.all([
                        (0, graphqlCache_1.cachedGraphql)({
                            query: queries_1.getSensorEventsBySensor,
                            variables: { sensorId: sensor.id, limit: EVENT_FETCH_LIMIT },
                        }),
                        (0, graphqlCache_1.cachedGraphql)({
                            query: queries_1.getSensorAlertsBySensor,
                            variables: { sensorId: sensor.id, limit: 10 },
                        }),
                    ]);
                    const events = eventResponse.data?.getSensorEventsBySensor?.items ||
                        eventResponse.data?.getSensorEventsBySensor ||
                        [];
                    const alerts = alertResponse.data?.getSensorAlertsBySensor?.items ||
                        alertResponse.data?.getSensorAlertsBySensor ||
                        [];
                    return {
                        sensorId: sensor.id,
                        events: events,
                        alerts: alerts,
                    };
                }
                catch {
                    return { sensorId: sensor.id, events: [], alerts: [] };
                }
            }));
            const map = new Map();
            results.forEach((r) => map.set(r.sensorId, r));
            setFetchResults((prev) => (0, stableValue_1.stable)(prev, map));
        }
        catch {
            // Keep existing
        }
        finally {
            setLoading(false);
        }
    }, [hubId, powerSensors.map((s) => s.id).join(',')]);
    (0, react_1.useEffect)(() => {
        fetchAllSensorData();
        intervalRef.current = setInterval(() => fetchAllSensorData(), POLLING_INTERVAL_MS);
        return () => {
            if (intervalRef.current)
                clearInterval(intervalRef.current);
        };
    }, [fetchAllSensorData]);
    const refreshOnAlertChange = (0, react_1.useCallback)(() => {
        fetchAllSensorData(true);
    }, [fetchAllSensorData]);
    (0, useAlertChangeRefresh_1.useAlertChangeRefresh)(refreshOnAlertChange);
    return (0, react_1.useMemo)(() => (0, energy_1.aggregateEnergySensors)(powerSensors, fetchResults, loading), [powerSensors, fetchResults, loading]);
};
exports.useEnergyAggregation = useEnergyAggregation;
